/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useMemo, useEffect } from "react";
import { buildApiUrl } from "./config/api";
import { FileUpload } from "./components/FileUpload";
import { Sidebar } from "./components/Sidebar";
import { EndpointView } from "./components/EndpointView";
import { Overview } from "./components/Overview";
import { OpenAPISpec } from "./types";
import { parseEndpoints } from "./utils/openapi";
import { SecurityModal } from "./components/SecurityModal";
import { AuthModal } from "./components/AuthModal";
import { Loader } from "./components/Loader";
import { Dashboard } from "./components/Dashboard";
import { PrivacyPolicy } from "./components/PrivacyPolicy";
import { TermsOfService } from "./components/TermsOfService";
import { Contact } from "./components/Contact";
import { DocNavbar } from "./components/DocNavbar";
import { AIChat } from "./components/AIChat";
import { Documentation } from "./components/Documentation";
import { AdminPanel } from "./components/AdminPanel";

type Page = "home" | "privacy" | "terms" | "contact" | "documentation";

export default function App() {
  const [spec, setSpec] = useState<OpenAPISpec | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [specId, setSpecId] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const [user, setUser] = useState<any>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("auth-token"),
  );
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<Page>("home");
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminKey, setAdminKey] = useState("");

  // Navigation function that updates browser history
  const navigate = (page: Page) => {
    setCurrentPage(page);
    const path = page === "home" ? "/" : `/${page}`;
    window.history.pushState({ page }, "", path);
    
    // Update document title for SEO
    const titles: Record<Page, string> = {
      home: "DocGether - Interactive API Documentation Generator",
      privacy: "Privacy Policy - DocGether",
      terms: "Terms of Service - DocGether",
      contact: "Contact Us - DocGether",
      documentation: "Documentation - DocGether",
    };
    document.title = titles[page];
  };

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === "/" || path === "") {
        setCurrentPage("home");
      } else if (path === "/privacy") {
        setCurrentPage("privacy");
      } else if (path === "/terms") {
        setCurrentPage("terms");
      } else if (path === "/contact") {
        setCurrentPage("contact");
      } else if (path === "/documentation") {
        setCurrentPage("documentation");
      } else if (path === "/7670") {
        // Show admin panel prompt for secret route
        const key = window.prompt("Enter admin key:");
        if (key) {
          setAdminKey(key);
          setShowAdminPanel(true);
        }
      } else {
        setCurrentPage("home");
      }
    };

    window.addEventListener("popstate", handlePopState);
    
    // Handle initial URL on page load
    const path = window.location.pathname;
    const titles: Record<string, string> = {
      "/privacy": "Privacy Policy - DocGether",
      "/terms": "Terms of Service - DocGether",
      "/contact": "Contact Us - DocGether",
      "/documentation": "Documentation - DocGether",
    };
    
    if (path === "/privacy") {
      setCurrentPage("privacy");
      document.title = titles[path];
    } else if (path === "/terms") {
      setCurrentPage("terms");
      document.title = titles[path];
    } else if (path === "/contact") {
      setCurrentPage("contact");
      document.title = titles[path];
    } else if (path === "/documentation") {
      setCurrentPage("documentation");
      document.title = titles[path];
    }

    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Secret admin panel - accessible via #7670 hash
  useEffect(() => {
    // Check URL hash for admin access on load
    if (window.location.hash === "#7670") {
      const key = window.prompt("Enter admin key:");
      if (key) {
        setAdminKey(key);
        setShowAdminPanel(true);
      }
      window.location.hash = ""; // Clear the hash
    }
    
    // Also check on hashchange
    const handleHashChange = () => {
      if (window.location.hash === "#7670") {
        const key = window.prompt("Enter admin key:");
        if (key) {
          setAdminKey(key);
          setShowAdminPanel(true);
        }
        window.location.hash = "";
      }
    };
    
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const res = await fetch(buildApiUrl("/api/auth/me"), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            setUser(data.user);
          } else {
            throw new Error("Auth failed");
          }
        } catch {
          setToken(null);
          localStorage.removeItem("auth-token");
        }
      }
      setIsAuthLoading(false);
    };
    checkAuth();
  }, [token]);

  // Handle OAuth callback (token from query params after backend redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get("token");
    const userStr = urlParams.get("user");
    const error = urlParams.get("error");

    if (error) {
      console.error(`OAuth failed: ${error}`);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (tokenParam && userStr) {
      try {
        const user = JSON.parse(decodeURIComponent(userStr));
        handleLogin(tokenParam, user);
        window.history.replaceState({}, "", window.location.pathname);
      } catch (e) {
        console.error("Failed to parse user data from OAuth callback");
      }
    }
  }, []);

  useEffect(() => {
    // Wait for auth check to complete before fetching spec
    if (isAuthLoading) return;

    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");

    if (id) {
      setSpecId(id);
      fetch(buildApiUrl(`/api/spec/${id}`))
        .then((res) => {
          if (res.ok) return res.json();
          throw new Error("Not found");
        })
        .then((data) => {
          setSpec(data);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          window.history.replaceState({}, "", "/");
        });
    } else {
      setIsLoading(false);
    }
  }, [isAuthLoading]);

  const handleSetSpec = async (newSpec: OpenAPISpec | null) => {
    if (newSpec && !user) {
      setIsAuthModalOpen(true);
      return;
    }

    setSpec(newSpec);
    if (newSpec) {
      try {
        const res = await fetch(buildApiUrl("/api/spec"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(newSpec),
        });
        const data = await res.json();
        if (data.success && data.id) {
          setSpecId(data.id);
          window.history.pushState({}, "", `?id=${data.id}`);
        }
      } catch (e) {
        console.error("Failed to save spec to server", e);
      }
    } else {
      setSpecId(null);
      window.history.pushState({}, "", "/");
      setSelectedEndpointId(null);
      setCredentials({});
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(
    null,
  );
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  const endpoints = useMemo(() => {
    if (!spec) return [];
    return parseEndpoints(spec);
  }, [spec]);

  const selectedEndpoint = useMemo(() => {
    return endpoints.find((e) => e.id === selectedEndpointId);
  }, [endpoints, selectedEndpointId]);

  const handleLogin = (newToken: string, newUser: any) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem("auth-token", newToken);
    setIsAuthModalOpen(false);
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("auth-token");
  };

  // Handle page routing for legal/info pages
  if (currentPage === "privacy") {
    return <PrivacyPolicy onBack={() => navigate("home")} />;
  }

  if (currentPage === "terms") {
    return <TermsOfService onBack={() => navigate("home")} />;
  }

  if (currentPage === "contact") {
    return <Contact onBack={() => navigate("home")} />;
  }

  if (currentPage === "documentation") {
    return <Documentation />;
  }

  // Admin panel can be shown on any page (even during loading)
  if (showAdminPanel) {
    return (
      <AdminPanel
        adminKey={adminKey}
        onClose={() => setShowAdminPanel(false)}
      />
    );
  }

  // Wait for both auth and initial data loading before rendering
  if (isLoading || isAuthLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <Loader size="lg" text="Loading" />
      </div>
    );
  }

  if (!spec) {
    // Show Dashboard for logged-in users, FileUpload for guests
    if (user) {
      return (
        <Dashboard
          token={token!}
          user={user}
          onSpecSelect={(id) => {
            window.history.pushState({}, "", `?id=${id}`);
            fetch(buildApiUrl(`/api/spec/${id}`))
              .then((res) => res.json())
              .then((data) => {
                setSpec(data);
                setSpecId(id);
              });
          }}
          onLogout={handleLogout}
        />
      );
    }

    return (
      <>
        <div className="absolute top-4 right-4 z-10">
            <button
              onClick={() => setIsAuthModalOpen(true)}
              className="bg-zinc-900 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-zinc-800 transition-colors shadow-sm"
            >
              Sign In
            </button>
          </div>
        <FileUpload
          onSpecLoaded={handleSetSpec}
          isLoggedIn={!!user}
          onLoginClick={() => setIsAuthModalOpen(true)}
          onNavigate={navigate}
        />
        {isAuthModalOpen && (
          <AuthModal
            onClose={() => setIsAuthModalOpen(false)}
            onLogin={handleLogin}
          />
        )}
      </>
    );
  }

  const hasSecuritySchemes =
    Object.keys(
      spec.components?.securitySchemes || spec.securityDefinitions || {},
    ).length > 0;

  return (
    <div className="flex flex-col h-screen bg-white text-zinc-900 overflow-hidden font-sans">
      {/* Top Navigation */}
      <DocNavbar
        user={user}
        onBackToDashboard={() => {
          handleSetSpec(null);
        }}
        onLogout={handleLogout}
        onShare={handleShare}
        isCopied={isCopied}
        specId={specId}
        onOpenAIChat={() => setIsAIChatOpen(true)}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        onLoginClick={() => setIsAuthModalOpen(true)}
      />

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden pt-16">
        <Sidebar
          spec={spec}
          endpoints={endpoints}
          selectedEndpointId={selectedEndpointId}
          onSelectEndpoint={setSelectedEndpointId}
          onSelectOverview={() => setSelectedEndpointId(null)}
          onUploadNew={() => handleSetSpec(null)}
          onAuthorize={() => setIsSecurityModalOpen(true)}
          credentials={credentials}
          specId={specId}
          hasSecuritySchemes={hasSecuritySchemes}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <main className="flex-1 overflow-y-auto bg-white w-full min-w-0">
          {selectedEndpoint ? (
            <div key={selectedEndpoint.id}>
              <EndpointView
                spec={spec}
                endpoint={selectedEndpoint}
                credentials={credentials}
                user={user}
              />
            </div>
          ) : (
            <Overview spec={spec} />
          )}
        </main>
      </div>

      {isSecurityModalOpen && (
        <SecurityModal
          spec={spec}
          credentials={credentials}
          onSave={setCredentials}
          onClose={() => setIsSecurityModalOpen(false)}
        />
      )}
      {isAuthModalOpen && (
        <AuthModal
          onClose={() => setIsAuthModalOpen(false)}
          onLogin={handleLogin}
        />
      )}

      {/* AI Chat */}
      <AIChat
        isOpen={isAIChatOpen}
        onClose={() => setIsAIChatOpen(false)}
        spec={spec}
        endpoints={endpoints}
        token={token}
      />
    </div>
  );
}
